import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "@/store/DataContext";
import {
  ChipGroup,
  CurrencySegment,
  Field,
  NumberInput,
  PrimaryButton,
  TextArea,
  TextInput,
} from "@/components/base/FormControls";
import SelectSheet, { type SelectOption } from "@/components/base/SelectSheet";
import { purchaseTotal } from "@/lib/metrics";
import { money, todayISO, uid } from "@/lib/format";
import type { Currency, Expense } from "@/lib/types";

const categories = [
  { key: "Transporte", label: "Transporte", icon: "ri-truck-line" },
  { key: "Combustible", label: "Combustible", icon: "ri-gas-station-line" },
  { key: "Envíos", label: "Envíos", icon: "ri-send-plane-line" },
  { key: "Almacenamiento", label: "Almacenaje", icon: "ri-archive-2-line" },
  { key: "Reparaciones", label: "Reparaciones", icon: "ri-tools-line" },
  { key: "Empaque", label: "Empaque", icon: "ri-box-3-line" },
  { key: "Otros gastos", label: "Otros gastos", icon: "ri-more-line" },
];

export default function NuevoGasto() {
  const { data, addExpense } = useData();
  const navigate = useNavigate();

  const [date, setDate] = useState(todayISO());
  const [category, setCategory] = useState("Transporte");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<Currency>(data.settings.presentationCurrency);
  const [linkedPurchaseId, setLinkedPurchaseId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const purchaseOptions: SelectOption[] = [
    { value: "", label: "Sin asociar", sub: "Gasto general del período" },
    ...data.purchases.map((p) => {
      const { total } = purchaseTotal(data, p.id);
      return {
        value: p.id,
        label: p.supplier || `Compra ${p.invoiceNumber || p.date}`,
        sub: p.date,
        trailing: money(total, p.currency),
      };
    }),
  ];

  const save = async () => {
    const value = parseFloat(amount);
    if (!description.trim() || !Number.isFinite(value) || value <= 0) {
      setError("Escribe una descripción y un importe mayor que cero.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const expense: Expense = {
        id: uid(),
        type: "expense",
        date,
        category,
        description: description.trim(),
        amount: value,
        currency,
        exchangeRate: currency === "USD" ? data.settings.exchangeRate : null,
        notes: notes.trim(),
        linkedPurchaseId: linkedPurchaseId || null,
        documentIds: [],
        createdAt: new Date().toISOString(),
      };
      await addExpense(expense);
      navigate("/movimientos");
    } catch (err) {
      setError("No se pudo guardar el gasto. Intenta de nuevo.");
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fr-animate space-y-5 pb-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200/80 text-foreground-700"
        >
          <i className="ri-arrow-left-line text-lg" />
        </button>
        <div>
          <h1 className="text-[20px] font-bold text-foreground-950">Registrar gasto</h1>
          <p className="text-[12px] text-foreground-500">Gastos adicionales del negocio</p>
        </div>
      </div>

      <Field label="Categoría">
        <ChipGroup options={categories} value={category} onChange={setCategory} />
      </Field>

      <Field label="Descripción">
        <TextInput
          value={description}
          onChange={setDescription}
          placeholder="Ej. Flete de la mercancía"
          icon="ri-file-text-line"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Moneda">
          <CurrencySegment value={currency} onChange={(v) => setCurrency(v as Currency)} />
        </Field>
        <Field label="Fecha">
          <TextInput value={date} onChange={setDate} type="date" icon="ri-calendar-line" />
        </Field>
      </div>

      <Field label="Importe">
        <NumberInput
          value={amount}
          onChange={setAmount}
          placeholder="0.00"
          prefix={currency === "USD" ? "US$" : "RD$"}
        />
      </Field>

      <Field label="Asociar a una compra (opcional)">
        <SelectSheet
          value={linkedPurchaseId}
          options={purchaseOptions}
          placeholder="Sin asociar"
          title="Asociar gasto a una compra"
          onChange={setLinkedPurchaseId}
        />
      </Field>

      <Field label="Notas (opcional)">
        <TextArea value={notes} onChange={setNotes} placeholder="Detalles del gasto..." />
      </Field>

      {error ? (
        <p className="rounded-2xl bg-red-50 px-4 py-3 text-[12px] text-red-600">{error}</p>
      ) : null}

      <PrimaryButton
        label={saving ? "Guardando..." : "Guardar gasto"}
        icon="ri-save-3-line"
        onClick={save}
        disabled={saving}
      />
    </div>
  );
}